import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { MailrailBaseEmailCampaign, TEmailCampaignMailing } from '@feathr/blackbox';
import { MailrailDripStep } from '@feathr/blackbox';
import {
  ButtonValid,
  CardActions,
  CardContent,
  CardHeader,
  CardV2,
  EmailInput,
  Fieldset,
  Modal,
  Spinner,
  toast,
  Tooltip,
} from '@feathr/components';
import PartnerSelect from '@feathr/extender/components/PartnerSelect';
import PersonSelect from '@feathr/extender/components/PersonSelect';
import { useStore } from '@feathr/extender/state';
import { flattenError } from '@feathr/hooks';
import { validate } from '@feathr/rachis';

import * as styles from './MailrailTestSendForm.css';

interface IBaseProps {
  /** The campaign that owns the send — provides `sendTestEmail`, the partner-vs-contact recipient
   * kind, and the `event` scope for the recipient lookup. */
  campaign: MailrailBaseEmailCampaign;
  /** The mailing under test: the campaign itself (the wizard Test step / enrollment card) or an
   * embedded drip step. Its template is previewed and its `mailing_id` (a step's `step_id`) scopes
   * the test send. */
  mailing: TEmailCampaignMailing;
  disabled?: boolean;
}

/** Renders as a card for the wizard Test step; the button sits in a `CardActions` footer. */
interface ICardVariantProps extends IBaseProps {
  variant?: 'card';
}

/** Renders as a self-contained Modal; the button sits in the modal's flush `rightActions` footer. */
interface IModalVariantProps extends IBaseProps {
  variant: 'modal';
  opened: boolean;
  onClose: () => void;
}

type IMailrailTestSendFormProps = ICardVariantProps | IModalVariantProps;

// Maximum number of recipients for a test email.
const MAX_EMAILS = 10;

/**
 * The shared test-send form. Renders as either a card or a modal depending on the `variant` prop.
 * Handles test sends for both campaigns and their embedded mailings (such as drip steps).
 *
 * @param props - {@link IMailrailTestSendFormProps}.
 */
function MailrailTestSendForm(props: Readonly<IMailrailTestSendFormProps>): JSX.Element {
  const { campaign, mailing, disabled = false } = props;
  const { Templates } = useStore();
  const [testEmail, setTestEmail] = useState<string | undefined>();
  const [testRecipientId, setTestRecipientId] = useState<string | undefined>();
  const [emailHadFocus, setEmailHadFocus] = useState(false);
  const [recipientHadFocus, setRecipientHadFocus] = useState(false);
  const { t } = useTranslation();

  const templateId = mailing.get('template_id');
  const template = templateId ? Templates.get(templateId) : undefined;
  // If a drip step was passed, we include its `step_id` in the request as `mailing_id`.
  // Otherwise we omit `mailing_id` to perform a test send for the campaign itself.
  // We will eventually have to devise a uniform way to get the mailing_ids of different types of mailings.
  const mailingId =
    mailing instanceof MailrailDripStep ? String(mailing.get('step_id')) : undefined;
  const testEmailList = testEmail?.split(',').map((s) => s.trim()) ?? [];

  function validateEmail(): string[] {
    const error = validate.single(testEmailList, {
      presence: { allowEmpty: false, message: '^Email addresses cannot be empty.' },
      length: {
        maximum: MAX_EMAILS,
        tooLong: `^You can only add up to ${MAX_EMAILS} email addresses.`,
      },
      list: {
        email: {
          message: '^An email address is invalid.',
        },
      },
    });
    return flattenError(error) ?? [];
  }

  function validateRecipient(): string[] {
    const error = validate.single(testRecipientId, {
      presence: {
        allowEmpty: false,
        message: campaign.isMailrailPartnerMessage
          ? '^Example partner cannot be empty.'
          : '^Example contact cannot be empty.',
      },
    });
    return error ?? [];
  }

  // All blocking errors for the send action, independent of field focus — drives both the button's
  // disabled state and its error tooltip.
  const sendErrors = [...validateRecipient(), ...validateEmail()];

  function handleBlurEmail(): void {
    setEmailHadFocus(true);
  }

  function handleBlurRecipient(): void {
    setRecipientHadFocus(true);
  }

  function handleClearRecipient(): void {
    setTestRecipientId(undefined);
  }

  async function handleSendTestEmail(): Promise<void> {
    await campaign.sendTestEmail({
      recipient_emails: testEmailList,
      recipient_id: testRecipientId!,
      template,
      mailingId,
    });
    toast(
      t(
        'Test email send scheduled. It should arrive at the provided email addresses in the next 5 - 10 minutes.',
      ),
    );
  }

  const emailAddressHelpText = (
    <div className={styles.emailHelpText}>
      <span>
        {t('You can add up to {{count}} comma-separated email address.', { count: MAX_EMAILS })}
      </span>
      <span>{`${testEmailList.length}/${MAX_EMAILS}`}</span>
    </div>
  );

  const fields = (
    <Fieldset>
      {campaign.isMailrailPartnerMessage ? (
        <PartnerSelect
          disabled={disabled}
          filters={{ _parent: campaign.get('event') }}
          label={
            <>
              {t('Example partner')}{' '}
              <Tooltip
                title={t(
                  'This example partner will be used to fill in merge fields that are included in your email and subject line.',
                )}
              >
                <FontAwesomeIcon icon={faInfoCircle} />
              </Tooltip>
            </>
          }
          onBlur={handleBlurRecipient}
          onChange={setTestRecipientId}
          onClear={handleClearRecipient}
          placeholder={t('Example partner')}
          validationError={recipientHadFocus ? validateRecipient() : []}
          value={testRecipientId}
        />
      ) : (
        <PersonSelect
          disabled={disabled}
          label={
            <>
              {t('Example contact')}{' '}
              <Tooltip
                title={t(
                  'This example contact will be used to fill in merge fields that are included in your email and subject line.',
                )}
              >
                <FontAwesomeIcon icon={faInfoCircle} />
              </Tooltip>
            </>
          }
          name={'example_contact'}
          onBlur={handleBlurRecipient}
          onChange={setTestRecipientId}
          placeholder={'Example contact'}
          validationError={recipientHadFocus ? validateRecipient() : []}
          value={testRecipientId}
        />
      )}
      <EmailInput
        allowMultiple={true}
        disabled={disabled}
        helpPlacement={'bottom'}
        helpText={emailAddressHelpText}
        label={t('Email addresses')}
        name={'test_email_address'}
        onBlur={handleBlurEmail}
        onChange={setTestEmail}
        placeholder={t('Add one or more emails separated by commas')}
        t={t}
        type={'email'}
        validationError={emailHadFocus ? validateEmail() : []}
        value={testEmail}
      />
    </Fieldset>
  );

  // While the template loads, show a spinner in place of the fields and suppress the footer button.
  const isLoading = template?.isPending ?? false;
  const body = isLoading ? <Spinner /> : fields;
  const sendButton = isLoading ? null : (
    <ButtonValid
      disabled={!template || sendErrors.length > 0}
      errors={sendErrors}
      name={'send_test_email'}
      onClick={handleSendTestEmail}
    >
      {t('Send test email')}
    </ButtonValid>
  );

  // eslint-disable-next-line react/destructuring-assignment
  if (props.variant === 'modal') {
    const { onClose, opened } = props;
    return (
      <Modal
        onClose={onClose}
        opened={opened}
        rightActions={sendButton}
        title={t('Send a test email')}
      >
        {body}
      </Modal>
    );
  }

  return (
    <CardV2>
      <CardHeader title={t('Send a test email')} />
      <CardContent>{body}</CardContent>
      {sendButton && <CardActions>{sendButton}</CardActions>}
    </CardV2>
  );
}

export default observer(MailrailTestSendForm);
